import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { Check, ChevronLeft, ChevronRight, Download, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, assetUrl, formatApiErrorDetail } from "@/lib/api";
import { ConfirmDelete } from "@/components/admin/shared";
import { RecordDialog } from "./RecordDialog";

const cell = (f, v) => {
  if (v == null || v === "") return <span className="text-slate-600">—</span>;
  if (f.type === "image") return <img src={assetUrl(v)} alt="" className="h-10 w-14 rounded border border-white/10 object-cover" />;
  if (f.type === "boolean") return v ? "Yes" : "No";
  return <span className="line-clamp-2 break-words">{String(v)}</span>;
};

const download = async (url, name) => {
  const r = await api.get(url, { responseType: "blob" });
  const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(r.data), download: name });
  a.click();
  URL.revokeObjectURL(a.href);
};

export const RecordsTable = ({ appId, collection, onChanged }) => {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const fields = collection.fields.slice(0, 5);
  const base = `/manage/apps/${appId}`;

  const load = useCallback(() => api.get(`${base}/collections/${collection.name}/records`, { params: { q: q || undefined, page } })
    .then((r) => setData(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))), [base, collection.name, q, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => setPage(1), [q, collection.name]);

  const refresh = () => { load(); onChanged?.(); };
  const approve = async (r) => {
    try { await api.patch(`${base}/records/${r.id}`, { status: "approved" }); toast.success("Approved — now visible on the app"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async () => {
    try { await api.delete(`${base}/records/${deleting.id}`); toast.success("Entry deleted"); setDeleting(null); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid={`records-${collection.name}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="flex min-w-[200px] flex-1 items-center gap-2 rounded-full border border-white/10 bg-[#07090E] px-3 py-2 text-sm focus-within:border-cyan-400/60">
          <Search className="h-4 w-4 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${collection.label.toLowerCase()}…`} data-testid="records-search-input" className="w-full bg-transparent text-white outline-none" />
        </label>
        <button onClick={() => setEditing({})} data-testid="records-new-button" className="inline-flex items-center gap-1.5 rounded-full bg-cyan-400 px-4 py-2 text-sm font-semibold text-[#07090E] hover:bg-cyan-300"><Plus className="h-4 w-4" /> New</button>
        <button onClick={() => download(`${base}/collections/${collection.name}/export.csv`, `${collection.name}.csv`)} data-testid="records-export-button" className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-sm text-slate-200 hover:border-cyan-400/50"><Download className="h-4 w-4" /> Export CSV</button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/5 bg-[#0B0F18]">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="border-b border-white/5 text-xs uppercase tracking-wider text-slate-500">
            <tr>{fields.map((f) => <th key={f.name} className="px-4 py-3 font-medium">{f.label}</th>)}<th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium">Added</th><th className="px-4 py-3" /></tr>
          </thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={r.id} data-testid={`record-row-${r.id}`} className="border-b border-white/5 text-slate-300 last:border-0 hover:bg-white/[0.02]">
                {fields.map((f) => <td key={f.name} className="max-w-[220px] px-4 py-3">{cell(f, r.data[f.name])}</td>)}
                <td className="px-4 py-3"><span data-testid={`record-status-${r.id}`} className={`rounded-full px-2 py-0.5 text-xs ${r.status === "pending" ? "bg-amber-400/10 text-amber-300" : "bg-emerald-400/10 text-emerald-300"}`}>{r.status === "pending" ? "Pending" : "Live"}</span></td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">{format(new Date(r.created_at), "MMM d, yyyy")}</td>
                <td className="whitespace-nowrap px-4 py-3 text-right">
                  {r.status === "pending" && <button onClick={() => approve(r)} data-testid={`record-approve-${r.id}`} className="mr-1 inline-flex items-center gap-1 rounded-full border border-emerald-400/40 px-2.5 py-1 text-xs text-emerald-300 hover:bg-emerald-400/10"><Check className="h-3 w-3" /> Approve</button>}
                  <button onClick={() => setEditing(r)} aria-label="Edit" data-testid={`record-edit-${r.id}`} className="rounded-lg p-1.5 text-slate-400 hover:text-cyan-300"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => setDeleting(r)} aria-label="Delete" data-testid={`record-delete-${r.id}`} className="rounded-lg p-1.5 text-slate-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={fields.length + 3} data-testid="records-empty" className="px-4 py-10 text-center text-slate-500">{q ? "No matching entries." : "No entries yet."}</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
        <span data-testid="records-total">{data.total} entr{data.total === 1 ? "y" : "ies"}</span>
        <span className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Previous page" data-testid="records-prev" className="rounded-full border border-white/10 p-1.5 disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
          <span data-testid="records-page">Page {data.page || 1} of {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} aria-label="Next page" data-testid="records-next" className="rounded-full border border-white/10 p-1.5 disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
        </span>
      </div>
      <RecordDialog appId={appId} collection={collection} record={editing?.id ? editing : null} open={!!editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); toast.success("Saved to the live app"); refresh(); }} />
      <ConfirmDelete target={deleting} label="this entry" onCancel={() => setDeleting(null)} onConfirm={remove} />
    </div>
  );
};
